import { Box, Button, Card, CircularProgress, Typography } from "@mui/material";
import React, { useState } from "react";
import { useParams } from "react-router-dom";

import BulkInviteEmailsModal from "../components/BulkInvite/BulkInviteEmailsModal";
import EventInviteShortlist from "../components/EventInviteShortlist";
import ShareByEmailDialog from "../components/ShareByEmailDialog";
import { Spacing } from "../types/enum";
import { useGetEventAdminQuery } from "../types/graphql";

/**
 * The event's Invite tab — reach members three ways: the smart shortlist (best-
 * matched members by the event's labels / zone, with Share + Invite and the
 * over-contacted guard), a plain email/CSV bulk invite, and share-by-email.
 */
const EventInvitePage: React.FC = () => {
  const { eventId } = useParams<Record<string, string>>();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const { data, loading } = useGetEventAdminQuery({
    fetchPolicy: "network-only",
    variables: { id: eventId ?? "" },
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={Spacing.xl}>
        <CircularProgress />
      </Box>
    );
  }

  const event = data?.getEventAdmin;
  if (!event) {
    return <Typography color="error">Event not found</Typography>;
  }

  return (
    <Box display="flex" flexDirection="column" gap={Spacing.m}>
      <Card variant="outlined">
        <Box
          p={Spacing.l}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={Spacing.m}
          flexWrap="wrap"
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              By email
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Have a list of emails? <strong>Invite</strong> adds them to this event;{" "}
              <strong>Share</strong> sends a heads-up without taking a spot.
            </Typography>
          </Box>
          <Box display="flex" gap={Spacing.s} flexWrap="wrap">
            <Button variant="outlined" onClick={() => setShareOpen(true)}>
              Share by email
            </Button>
            <Button variant="outlined" onClick={() => setInviteOpen(true)}>
              Bulk invite by email
            </Button>
          </Box>
          <BulkInviteEmailsModal open={inviteOpen} onClose={() => setInviteOpen(false)} />
          {eventId && (
            <ShareByEmailDialog
              open={shareOpen}
              onClose={() => setShareOpen(false)}
              eventId={eventId}
            />
          )}
        </Box>
      </Card>

      <EventInviteShortlist
        eventId={event.id}
        title={event.title ?? "this event"}
        maxParticipants={event.maxParticipants}
      />
    </Box>
  );
};

export default EventInvitePage;
